import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BillType, ITables, TABLE_STATUS } from '../../../all-types'
import {
  addTableItem,
  createLatestBill,
  getTableById,
  removeTableItem,
  updateCartItemOfTheTable,
  updateTableItem,
} from '../../../services'
import { defaultBillType } from '../bill-cart'
import { BillItem } from '../../../all-types'

// Per-item monotonic version using the server-stamped `kitchInfo.updatedAt`.
// Live `tables:one:<id>` socket echoes can arrive out of order (a snapshot
// from an older PUT landing after a newer one). Merging on this timestamp
// keeps an item that has already advanced from being reverted by a stale
// echo — the same protection the preload-data list reducer already uses.
const itemTime = (i?: BillItem | null): number => {
  if (!i?.kitchInfo?.updatedAt) return 0
  const t = new Date(i.kitchInfo.updatedAt as unknown as string).getTime()
  return Number.isFinite(t) ? t : 0
}

const mergeSelectedTable = (
  current: ITables | null,
  incoming: ITables,
  pendingCartItemSnos: Record<number, true> = {}
): ITables => {
  if (!current?._id || current._id !== incoming._id) return incoming
  const cItems = current.billSession?.items
  const iItems = incoming.billSession?.items
  if (!cItems?.length || !iItems?.length) return incoming
  const bySno = new Map<number, BillItem>()
  for (const it of cItems) bySno.set(it.sno, it)
  const incomingSnos = new Set<number>()
  const merged = iItems.map((inc) => {
    incomingSnos.add(inc.sno)
    const cur = bySno.get(inc.sno)
    if (!cur) return inc
    return itemTime(cur) > itemTime(inc) ? cur : inc
  })
  for (const cur of cItems) {
    if (pendingCartItemSnos[cur.sno] && !incomingSnos.has(cur.sno)) {
      merged.push(cur)
    }
  }
  return {
    ...incoming,
    billSession: { ...incoming.billSession!, items: merged },
  }
}

const initialState: {
  selectedTable: ITables | null
  createdBill: BillType | null
  isTakeAway: boolean
  pendingCartItemSnos: Record<number, true>
} = {
  selectedTable: null,
  createdBill: null,
  isTakeAway: false,
  pendingCartItemSnos: {},
}

export const TableSession = createSlice({
  name: 'table-session',
  initialState,
  reducers: {
    setSelectedTable: (state, action: PayloadAction<ITables | null>) => {
      if (action.payload === null) {
        state.selectedTable = null
        state.createdBill = null
        return
      }

      const billSession = action.payload?.billSession

      if (!billSession) {
        state.selectedTable = {
          ...action.payload,
          billSession: defaultBillType,
        }
      } else {
        state.selectedTable = action.payload
      }
    },
    // P1-7: dedicated path for live `tables:one` socket echoes. Unlike
    // setSelectedTable (which trusts its caller for optimistic local
    // updates), this routes the incoming snapshot through the updatedAt
    // merge so a stale echo cannot revert a newer item state. A null/empty
    // echo is ignored so it can never wipe the open table.
    mergeLiveTable: (state, action: PayloadAction<ITables | null>) => {
      const incoming = action.payload
      if (!incoming?._id) return
      const merged = mergeSelectedTable(
        state.selectedTable,
        incoming,
        state.pendingCartItemSnos
      )
      if (!merged.billSession) {
        state.selectedTable = { ...merged, billSession: defaultBillType }
      } else {
        state.selectedTable = merged
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTableById.fulfilled, (state, action) => {
        // Drop the response if the user has already closed the table or
        // switched to a different one. Without this guard, a stale
        // GET that resolves after `setSelectedTable(null)` would
        // "ghost-reopen" the table the user just closed.
        if (
          state.selectedTable === null ||
          (action.payload?._id && state.selectedTable._id !== action.payload._id)
        ) {
          return
        }

        if (action.payload === null) {
          state.selectedTable = null
          return
        }

        const billSession = action.payload?.billSession

        if (!billSession) {
          state.selectedTable = {
            ...action.payload,
            billSession: defaultBillType,
          }
        } else {
          state.selectedTable = action.payload
        }
      })
      .addCase(updateCartItemOfTheTable.fulfilled, (state, action) => {
        if (!action.payload) return
        // Same guard as above. The close-table flow fires a background
        // PUT to free the billSession after `setSelectedTable(null)`;
        // letting its fulfilled response land would reopen the table
        // ~1s later (matching the PUT's round-trip).
        if (
          state.selectedTable === null ||
          state.selectedTable._id !== action.payload._id
        ) {
          return
        }
        state.selectedTable = action.payload
      })
      .addCase(createLatestBill.fulfilled, (state, action) => {
        state.createdBill = action.payload
      })
      .addMatcher(
        (action) =>
          [
            addTableItem.pending.type,
            updateTableItem.pending.type,
            removeTableItem.pending.type,
          ].includes(action.type),
        (state, action: any) => {
          const arg = action.meta?.arg
          const sno = arg?.sno ?? arg?.item?.sno
          if (typeof sno === 'number' && sno > 0) {
            state.pendingCartItemSnos[sno] = true
          }
        }
      )
      // Granular single-item mutations reflect into the selected table the
      // same way as the whole-table PUT (skip null + same-table guard).
      // (addMatcher must come after every addCase.)
      .addMatcher(
        (action) =>
          [
            addTableItem.fulfilled.type,
            updateTableItem.fulfilled.type,
            removeTableItem.fulfilled.type,
          ].includes(action.type),
        (state, action: PayloadAction<ITables | null>) => {
          const arg = (action as any).meta?.arg
          const sno = arg?.sno ?? arg?.item?.sno
          if (typeof sno === 'number') {
            delete state.pendingCartItemSnos[sno]
          }
          if (!action.payload) return
          if (
            state.selectedTable === null ||
            state.selectedTable._id !== action.payload._id
          ) {
            return
          }
          state.selectedTable = mergeSelectedTable(
            state.selectedTable,
            action.payload,
            state.pendingCartItemSnos
          )
        }
      )
      .addMatcher(
        (action) =>
          [
            addTableItem.rejected.type,
            updateTableItem.rejected.type,
            removeTableItem.rejected.type,
          ].includes(action.type),
        (state, action: any) => {
          const arg = action.meta?.arg
          const sno = arg?.sno ?? arg?.item?.sno
          if (typeof sno === 'number') {
            delete state.pendingCartItemSnos[sno]
          }
        }
      )
  },
})

export const { setSelectedTable, mergeLiveTable } = TableSession.actions
